import { Injectable } from '@angular/core';
import * as Excel from 'exceljs';
import { ExcelSheets } from '../../interfaces/excel-sheets.interface';
import { RawCellContent } from 'hyperformula';
import { EXCEL_FILE_STANDARD_DATA } from '../../constants';
import { HyperFormulaDataService } from '../hyper-formula-data/hyper-formula-data.service';

@Injectable({
  providedIn: 'root',
})
export class HyperFormulaParserService {
  constructor(private _hfDataService: HyperFormulaDataService) {}

  parseExcelSheets(
    excelSheets: ExcelSheets,
    namedRanges: Excel.DefinedNamesModel,
  ) {
    const excelSheetsTemp = { ...excelSheets };
    const entries = Object.entries(excelSheetsTemp);

    this._hfDataService.clear();

    // required to start by Parameters sheet to avoid missing references in Scenario
    // same logic for Scenario, which is referenced in other sheets
    entries.sort(([keyA], [keyB]) => {
      const { SCENARIO, PARAMETERS, RESULTS, CARE_PATHWAY, SUMMARY } =
        EXCEL_FILE_STANDARD_DATA.SHEET_NAMES;

      const getKnownPriority = (name: string) => {
        if (name === SCENARIO) return 1001;
        if (name === PARAMETERS) return 1002;
        if (name === RESULTS) return 2001;
        if (name === CARE_PATHWAY) return 2002;
        if (name === SUMMARY) return 2003;
        return -1; // unknown
      };

      const parseUnknown = (name: string) => {
        const match = name.match(/^(\d+)_/);
        if (!match)
          return { index: Number.MAX_SAFE_INTEGER, isFirstGroup: false };

        const num = parseInt(match[1], 10);
        return { index: num, isFirstGroup: match[1].startsWith('0') };
      };

      const priA = getKnownPriority(keyA);
      const priB = getKnownPriority(keyB);

      // Both unknowns
      if (priA === -1 && priB === -1) {
        const ua = parseUnknown(keyA);
        const ub = parseUnknown(keyB);

        // First-group unknowns always before second-group unknowns
        if (ua.isFirstGroup && !ub.isFirstGroup) return -1;
        if (!ua.isFirstGroup && ub.isFirstGroup) return 1;

        // Within the same group → sort by numeric index
        if (ua.index !== ub.index) return ua.index - ub.index;

        // Same index → keep original order
        return 0;
      }

      // One unknown, one known → determine placement based on known's priority range
      if (priA === -1) {
        const ua = parseUnknown(keyA);
        // First-group unknowns before scenario (1001+), second-group before results (2001+)
        if (ua.isFirstGroup) return -1; // unknown before known
        return priB >= 2001 ? -1 : 1; // second group unknown before results
      }
      if (priB === -1) {
        const ub = parseUnknown(keyB);
        if (ub.isFirstGroup) return 1;
        return priA >= 2001 ? 1 : -1;
      }

      // Both known sheets → sort by fixed priority
      return priA - priB;
    });

    for (const [sheetName, rows] of entries) {
      this._addSheet(sheetName, rows);
      this._hfDataService.sheetIdMapping.push(sheetName);
    }
    this._registerNamedExpressions(namedRanges);
  }

  private _addSheet(sheetName: string, rows: Excel.Row[] | undefined) {
    const instancedSheetName = this._hfDataService.instance.addSheet(sheetName);
    const instancedSheetId =
      this._hfDataService.instance.getSheetId(instancedSheetName);

    if (instancedSheetId === undefined) {
      throw new Error('instancedSheetId is undefined.');
    }

    const convertedRows = this._convertExcelRowsToHFContent(rows);

    this._hfDataService.instance.setSheetContent(
      instancedSheetId,
      convertedRows,
    );
  }

  private _convertExcelRowsToHFContent(
    rows: Excel.Row[] | undefined,
  ): RawCellContent[][] {
    if (!rows) return [];

    return rows.map((row) => {
      const cellValues: RawCellContent[] = [];

      row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
        const normalizedValue = this._normalizeCellValue(cell);
        cellValues[colNumber - 1] = normalizedValue;
      });

      return cellValues;
    });
  }

  private _normalizeCellValue(cell: Excel.Cell): RawCellContent {
    const value = cell.value;

    if (value == null) return null;

    // Handle formula explicitly if not using the `cell.formula` field directly
    if (cell.formula) {
      return `=${cell.formula}`;
    }

    // Handle rich text: extract and concatenate text parts
    if (
      typeof value === 'object' &&
      'richText' in value &&
      Array.isArray(value.richText)
    ) {
      return value.richText.map((part: any) => part.text).join('');
    }

    // Handle hyperlinks (if ever used)
    if (
      typeof value === 'object' &&
      'text' in value &&
      typeof value.text === 'string'
    ) {
      return value.text;
    }

    // Handle known primitive types
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return value;
    }

    // Fallback: stringify anything else
    return String(value);
  }

  private _registerNamedExpressions(namedRanges: Excel.DefinedNamesModel) {
    const hf = this._hfDataService.instance;

    namedRanges.forEach((definedName) => {
      const name = definedName.name;

      const ref = definedName.ranges[0];
      if (!ref) return;

      const [sheetNameRaw, address] = ref.split('!');
      if (!sheetNameRaw || !address) return;

      const sheetName = sheetNameRaw.split("'")[1];
      const sheetId = hf.getSheetId(sheetName);
      if (sheetId === undefined) {
        console.warn(
          `Skipping named range '${name}': Sheet '${sheetName}' not found`,
        );
        return;
      }

      const formula = `=${sheetName}!${address}`;

      try {
        hf.addNamedExpression(name, formula);
      } catch (e) {
        console.warn(
          `Could not register named expression ${name} with formula ${formula}:`,
          e,
        );
      }
    });
  }
}
